import { useEffect, useState } from 'react'
import { ProjectSettingsLayout } from './ProjectSettingsLayout'
import { dashboardSitePages, type Page } from '../../PageManager/types'
import type { CmsCollection } from '../cmsTypes'
import { fetchTeam, getAccountUsage, getBlog, listContacts, listOffers, type AccountUsage, type BillingPlan } from '../../../lib/api'
import { SITE_PLAN_LIMITS, PLAN_LABEL, formatPlanLimit } from '../../../lib/planLimits'
import { activeLanguage, useI18n } from '../../../lib/i18n'
import { IconAddressBook, IconArticle, IconArrowRight, IconDatabase, IconDeviceSdCard, IconFileText, IconFiles, IconGauge, IconShoppingCart, IconStack2, IconUsers } from '@tabler/icons-react'
import { MascotIcon } from '../../../lib/mascotIcon'
import { UsageStatCard, fmtBytes, type UsageStat } from './UsageStatCard'

function fmtCycleDate(iso: string | null): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString(activeLanguage(), { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return ''
  }
}

/**
 * Los topes de ESTE sitio.
 *
 * Enseñaba también la IA, el almacenamiento, el tráfico y los formularios, y
 * ésos no son del sitio: se cuentan por persona, contra una sola bolsa. Con
 * tres proyectos, los tres enseñaban la misma barra como si fuera suya. Eso
 * vive ahora en «Uso de la cuenta» (`AccountUsage`); aquí queda un resumen
 * con el enlace, para que nadie crea que cada sitio trae su propia bolsa.
 */
export function SiteUsage({ projectId, plan, pages, collections, periodStart, periodEnd, token, onOpenPlans, onOpenAccountUsage, onBack }: {
  projectId: string
  plan: BillingPlan
  pages: Page[]
  collections: CmsCollection[]
  periodStart: string | null
  periodEnd: string | null
  token: string
  onOpenPlans: () => void
  /** «Uso de la cuenta»: la bolsa común que este sitio comparte con los demás. */
  onOpenAccountUsage: () => void
  onBack?: (() => void) | undefined
}) {
  const { t } = useI18n()
  /* Sólo para el resumen de la bolsa común, abajo. */
  const [usage, setUsage] = useState<AccountUsage | null>(null)
  useEffect(() => {
    let alive = true
    getAccountUsage(token).then((u) => { if (alive) setUsage(u) }).catch(() => {})
    return () => { alive = false }
  }, [token])

  /*
   * Los otros recursos con tope: ofertas, entradas del blog, contactos e invitados.
   *
   * Faltaban en esta pantalla —la que existe para decir cuánto llevas gastado—
   * mientras el servidor sí les aplicaba su tope. Enterarse de que no puedes
   * crear el contacto 101 al intentarlo es peor que verlo venir.
   *
   * Se piden aquí y no llegan por propiedades como las páginas porque quien
   * monta esta pantalla no los tiene: el taller carga páginas y colecciones, y
   * traerse el catálogo entero, los contactos y el equipo hasta aquí sólo para
   * contar cuatro números los cargaría en todas las demás secciones también.
   *
   * En paralelo y sin bloquear: cada fila aparece cuando llega la suya, y si
   * una petición falla se calla —esto es un resumen, no una pantalla que deba
   * romperse porque un contador no llegó.
   */
  const [publishedOffers, setPublishedOffers] = useState<number | null>(null)
  const [contacts, setContacts] = useState<number | null>(null)
  const [seats, setSeats] = useState<number | null>(null)
  const [blogPosts, setBlogPosts] = useState<{ published: number; limit: number | null } | null>(null)
  useEffect(() => {
    let alive = true
    void listOffers(projectId, token)
      .then((rows) => {
        if (!alive) return
        /* La misma regla que el servidor: cuentan las PUBLICADAS, de cualquier
           familia. Un borrador no lo ve nadie de fuera, así que no vende y no
           ocupa hueco — y el tope tampoco lo cuenta. */
        setPublishedOffers(rows.filter((row) => row.status === 'active').length)
      })
      .catch(() => {})
    /* Una página de uno: del sobre sólo interesa `total`. */
    void listContacts(projectId, token, { pageSize: 1 })
      .then((page) => { if (alive) setContacts(page.total) })
      .catch(() => {})
    /* Las publicadas, como cuenta el servidor al publicar: el recuento llega
       ya hecho con la lista del blog. */
    void getBlog(projectId, token)
      .then((blog) => { if (alive) setBlogPosts(blog.usage) })
      .catch(() => {})
    void fetchTeam(projectId, token)
      .then((team) => { if (alive) setSeats(team.seats.used) })
      .catch(() => {})
    return () => { alive = false }
  }, [projectId, token])
  const limits = SITE_PLAN_LIMITS[plan]
  const cycleLabel = periodStart && periodEnd
    ? `${fmtCycleDate(periodStart)} – ${fmtCycleDate(periodEnd)}`
    : periodEnd
      ? t('usage.renewsOn', { date: fmtCycleDate(periodEnd) })
      : plan === 'free'
        ? t('usage.freeNoCycle')
        : t('usage.noCycle')
  const pagesCount = dashboardSitePages(pages).length
  const collectionsCount = collections.length
  const cmsItemsCount = collections.reduce((total, collection) => total + collection.items.length, 0)

  // Real, plan-driven limits (drive the upsell). Unlimited when `limit` is null.
  const planStats: UsageStat[] = [
    { id: 'pages', label: t('usage.stat.pages'), used: pagesCount, limit: limits.pages, icon: IconFileText, description: t('usage.stat.pagesBody') },
    { id: 'collections', label: t('usage.stat.collections'), used: collectionsCount, limit: limits.collections, icon: IconDatabase, description: t('usage.stat.collectionsBody') },
    { id: 'cmsItems', label: t('usage.stat.cmsItems'), used: cmsItemsCount, limit: limits.cmsItems, icon: IconFiles, description: t('usage.stat.cmsItemsBody') },
    /* Los que se piden aparte se pintan sólo cuando su recuento llegó: con un cero de
       relleno, quien tiene ochenta contactos vería «0 de 100» durante un
       instante — y ese instante es justo cuando se está mirando. */
    ...(publishedOffers === null ? [] : [{ id: 'offers' as const, label: t('usage.stat.offers'), used: publishedOffers, limit: limits.offers, icon: IconShoppingCart, description: t('usage.stat.offersBody') }]),
    ...(blogPosts === null ? [] : [{ id: 'blogPosts' as const, label: t('usage.stat.blogPosts'), used: blogPosts.published, limit: blogPosts.limit, icon: IconArticle, description: t('usage.stat.blogPostsBody') }]),
    ...(contacts === null ? [] : [{ id: 'contacts' as const, label: t('usage.stat.contacts'), used: contacts, limit: limits.contacts, icon: IconAddressBook, description: t('usage.stat.contactsBody') }]),
    ...(seats === null ? [] : [{ id: 'teamMembers' as const, label: t('usage.stat.team'), used: seats, limit: limits.teamMembers, icon: IconUsers, description: t('usage.stat.teamBody') }]),
  ]

  /* El resumen de la bolsa común: tres cifras, no tarjetas. Las tarjetas están
     en «Uso de la cuenta»; repetirlas aquí volvería a hacerlas parecer del sitio. */
  const percentOf = (used: number, limit: number | null) => (limit ? `${Math.min(100, Math.round((used / limit) * 100))}%` : '∞')
  const shared = usage ? [
    { id: 'ai', icon: MascotIcon, label: t('usage.stat.ai'), value: percentOf(usage.used.aiTokens, usage.limits.ai_tokens) },
    { id: 'generations', icon: MascotIcon, label: t('usage.stat.generations'), value: `${usage.used.aiCredits.toLocaleString(activeLanguage())} / ${formatPlanLimit(usage.limits.ai_credits)}` },
    { id: 'storage', icon: IconDeviceSdCard, label: t('usage.stat.storage'), value: `${fmtBytes(usage.used.storageBytes)} / ${fmtBytes(usage.limits.storage_bytes)}` },
  ] : []

  return (
    <ProjectSettingsLayout title={t('usage.title')} description={t('usage.description')} onBack={onBack}>
      <section className="dash-settings-card dash-general-card dash-project-usage-limits">
        <div className="dash-settings-card-head dash-usage-section-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconGauge size={18} /></span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('usage.planConsumption', { plan: PLAN_LABEL[plan] })}</h3>
            <p>{t('usage.currentCycle', { cycle: cycleLabel })}</p>
          </div>
          <button type="button" className="dash-secondary-btn dash-usage-plans-btn" onClick={onOpenPlans}>{t('plans.over.seePlans')}</button>
        </div>

        <div className="dash-site-usage-group">
          <h4>{t('usage.groupContent')}</h4>
          <div className="dash-site-usage-grid dash-site-usage-grid--content">
            {planStats.map((stat) => <UsageStatCard key={stat.id} stat={stat} plan={plan} onOpenPlans={onOpenPlans} />)}
          </div>
        </div>

        <div className="dash-usage-shared">
          <span className="dash-widget-glass-icon dash-usage-card-icon" aria-hidden="true"><IconStack2 size={18} /></span>
          <div className="dash-usage-shared-copy">
            <strong>{t('usage.shared.title')}</strong>
            <p>{t('usage.shared.body')}</p>
            {shared.length ? (
              <ul className="dash-usage-shared-stats">
                {shared.map((item) => (
                  <li key={item.id}>
                    <item.icon size={13} aria-hidden="true" />
                    <span>{item.label}</span>
                    <b>{item.value}</b>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <button type="button" className="dash-secondary-btn dash-usage-shared-open" onClick={onOpenAccountUsage}>
            {t('usage.shared.open')}
            <IconArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
      </section>
    </ProjectSettingsLayout>
  )
}
